import { request } from "@/services/gql";

const CREATE_UPLOAD_LINK_MUTATION = `
  mutation Mutation($modelType: String!, $field: String!, $quizCode: String, $originalFilename: String) {
    createUploadLink(model_type: $modelType, field: $field, quiz_code: $quizCode, original_filename: $originalFilename) {
      headers
      method
      upload_token
      upload_url
    }
  }
`;

const UPSERT_UNSPLASH_FILE_MUTATION = `
  mutation UpsertUnsplashFile(
    $id: ID
    $quizCode: String
    $modelType: String
    $modelId: ID
    $field: String
    $unsplashImage: UnsplashImageInput!
  ) {
    upsertUnsplashFile(
      id: $id
      quiz_code: $quizCode
      model_type: $modelType
      model_id: $modelId
      field: $field
      unsplash_image: $unsplashImage
    ) {
      id
      upload_token
      quiz_code
      storage
      model_type
      model_id
      field
      filename
      path
      status
      unsplash_requirements {
        photoId
        imageUrl
        downloadLocation
        authorName
        authorUrl
        unsplashUrl
        attributionHtml
      }
    }
  }
`;

const MODEL_TYPE_MAP = {
    benefit: "App\\Models\\Benefit",
    finish_page: "App\\Models\\FinishPage",
    offer: "App\\Models\\Offer",
    offer_page: "App\\Models\\OfferPage",
    question: "App\\Models\\Question",
    quiz: "App\\Models\\Quiz",
    result_page_result: "App\\Models\\ResultPageResult",
    start_page: "App\\Models\\StartPage",
    variant: "App\\Models\\Variant",
};

const FORBIDDEN_UPLOAD_HEADERS = new Set(["host", "content-length"]);

const DEFAULT_DISPLAY_SETTINGS = {
    desktop: {
        background_size: "cover",
        horizontal_position: 50,
        vertical_position: 50,
    },
    mobile: {
        background_size: "cover",
        horizontal_position: 50,
        vertical_position: 50,
    },
};

function normalizeHeaders(headers) {
    const normalized = {};

    if (!headers || typeof headers !== "object") {
        return normalized;
    }

    Object.entries(headers).forEach(([name, value]) => {
        if (value == null) {
            return;
        }

        if (Array.isArray(value)) {
            if (value[0] != null) {
                normalized[name] = String(value[0]);
            }
            return;
        }

        normalized[name] = String(value);
    });

    return normalized;
}

function sanitizeUploadHeaders(headers) {
    const sanitized = {};

    Object.entries(headers).forEach(([name, value]) => {
        if (!name || value == null || value === "") {
            return;
        }

        if (FORBIDDEN_UPLOAD_HEADERS.has(name.toLowerCase())) {
            return;
        }

        sanitized[name] = value;
    });

    return sanitized;
}

function cloneDisplaySettings(displaySettings) {
    const desktop = displaySettings?.desktop || {};
    const mobile = displaySettings?.mobile || {};

    return {
        desktop: {
            background_size: desktop.background_size || DEFAULT_DISPLAY_SETTINGS.desktop.background_size,
            horizontal_position: Number.isFinite(Number(desktop.horizontal_position))
                ? Number(desktop.horizontal_position)
                : DEFAULT_DISPLAY_SETTINGS.desktop.horizontal_position,
            vertical_position: Number.isFinite(Number(desktop.vertical_position))
                ? Number(desktop.vertical_position)
                : DEFAULT_DISPLAY_SETTINGS.desktop.vertical_position,
        },
        mobile: {
            background_size: mobile.background_size || DEFAULT_DISPLAY_SETTINGS.mobile.background_size,
            horizontal_position: Number.isFinite(Number(mobile.horizontal_position))
                ? Number(mobile.horizontal_position)
                : DEFAULT_DISPLAY_SETTINGS.mobile.horizontal_position,
            vertical_position: Number.isFinite(Number(mobile.vertical_position))
                ? Number(mobile.vertical_position)
                : DEFAULT_DISPLAY_SETTINGS.mobile.vertical_position,
        },
    };
}

export function getPendingUploadToken(media) {
    if (!media) {
        return null;
    }

    if (typeof media === "string") {
        return media;
    }

    return media.upload_token || null;
}

export function getModelTypeName(modelType) {
    return MODEL_TYPE_MAP[modelType] || modelType;
}

export function createPendingMediaFile({
    uploadToken,
    previewUrl,
    existingFile = null,
    includeDisplaySettings = true,
}) {
    const pendingFile = {
        id: existingFile?.id ?? null,
        filename: previewUrl || existingFile?.filename || "",
        hint: existingFile?.hint || "",
        upload_token: uploadToken,
    };

    if (existingFile?.storage === "unsplash" && existingFile?.id) {
        pendingFile.replaced_unsplash_file = existingFile;
    }

    if (includeDisplaySettings) {
        pendingFile.display_settings = cloneDisplaySettings(existingFile?.display_settings);
    }

    return pendingFile;
}

function buildUnsplashAttribution(image) {
    if (!image?.authorName) {
        return "";
    }

    return `Photo by ${image.authorName} on Unsplash`;
}

export function isValidUnsplashImage(image) {
    const required = [image?.photoId || image?.id, image?.imageUrl || image?.url, image?.downloadLocation];
    return required.every(value => typeof value === "string" && value.trim().length > 0);
}

function buildUnsplashImageInput(image) {
    if (!isValidUnsplashImage(image)) {
        throw new Error("Unsplash image is missing required photoId, imageUrl or downloadLocation");
    }
    return {
        photoId: image.photoId || image.id,
        imageUrl: image.imageUrl || image.url,
        downloadLocation: image.downloadLocation,
        authorName: image.authorName || image.label || "",
        authorUrl: image.authorUrl || "",
        unsplashUrl: image.unsplashUrl || "",
        attributionHtml: image.attributionHtml || buildUnsplashAttribution(image),
    };
}

function getUnsplashImageFromMedia(media) {
    const requirements = media?.unsplash_requirements;

    if (!requirements) {
        return null;
    }

    return {
        photoId: requirements.photoId,
        imageUrl: requirements.imageUrl || media.filename,
        downloadLocation: requirements.downloadLocation,
        authorName: requirements.authorName,
        authorUrl: requirements.authorUrl,
        unsplashUrl: requirements.unsplashUrl,
        attributionHtml: requirements.attributionHtml,
    };
}

function createMediaFromUnsplashFile({ file, existingFile = null, includeDisplaySettings = true }) {
    const media = {
        id: file.id ?? null,
        filename: file.filename || file.unsplash_requirements?.imageUrl || existingFile?.filename || "",
        hint: existingFile?.hint || "",
        storage: file.storage || "unsplash",
        upload_token: file.upload_token || null,
        unsplash_requirements: file.unsplash_requirements || null,
    };

    if (includeDisplaySettings) {
        media.display_settings = cloneDisplaySettings(existingFile?.display_settings);
    }

    return media;
}

export async function upsertUnsplashFile({
    id = null,
    quizCode = null,
    modelType = null,
    modelId = null,
    field = null,
    image,
}) {
    const data = await request({
        query: UPSERT_UNSPLASH_FILE_MUTATION,
        variables: {
            id,
            quizCode,
            modelType: modelType ? getModelTypeName(modelType) : null,
            modelId,
            field,
            unsplashImage: buildUnsplashImageInput(image),
        },
    });

    return data?.upsertUnsplashFile;
}

export async function createUnsplashMediaFile({
    image,
    modelType,
    modelId = null,
    field,
    quizCode,
    existingFile = null,
    includeDisplaySettings = true,
}) {
    const canUpdateExistingUnsplash = existingFile?.storage === "unsplash" && existingFile?.id;
    const file = await upsertUnsplashFile({
        id: canUpdateExistingUnsplash ? existingFile.id : null,
        quizCode: canUpdateExistingUnsplash ? null : quizCode,
        modelType: canUpdateExistingUnsplash ? null : modelType,
        modelId: canUpdateExistingUnsplash ? null : modelId,
        field: canUpdateExistingUnsplash ? null : field,
        image,
    });

    if (!file?.id && !file?.upload_token) {
        throw new Error("Invalid Unsplash file payload");
    }

    return createMediaFromUnsplashFile({
        file,
        existingFile,
        includeDisplaySettings,
    });
}

export async function unlinkUnsplashMediaFile(media) {
    if (media?.storage !== "unsplash" || !media?.id) {
        return null;
    }

    const image = getUnsplashImageFromMedia(media);

    if (!image) {
        return null;
    }

    return upsertUnsplashFile({
        id: media.id,
        quizCode: null,
        modelType: null,
        modelId: null,
        field: null,
        image,
    });
}

export async function createUploadLink({ modelType, field, quizCode, originalFilename }) {
    if (!modelType) {
        throw new Error("modelType is required");
    }

    if (!field) {
        throw new Error("field is required");
    }

    const data = await request({
        query: CREATE_UPLOAD_LINK_MUTATION,
        variables: {
            modelType,
            field,
            quizCode: quizCode || null,
            originalFilename: originalFilename || null,
        },
    });

    const payload = data?.createUploadLink;
    if (!payload?.upload_url || !payload?.upload_token) {
        throw new Error("Invalid upload link payload");
    }

    return {
        ...payload,
        method: payload.method || "PUT",
        headers: normalizeHeaders(payload.headers),
    };
}

export async function uploadFileToLink({ file, uploadUrl, method = "PUT", headers = {} }) {
    if (!file) {
        throw new Error("file is required");
    }
    if (!uploadUrl) {
        throw new Error("uploadUrl is required");
    }

    const response = await fetch(uploadUrl, {
        method,
        headers: sanitizeUploadHeaders(headers),
        body: file,
    });

    if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
    }
}

export async function uploadFileWithToken({ file, modelType, field, quizCode }) {
    const uploadLink = await createUploadLink({
        modelType,
        field,
        quizCode,
        originalFilename: file?.name || null,
    });
    await uploadFileToLink({
        file,
        uploadUrl: uploadLink.upload_url,
        method: uploadLink.method,
        headers: uploadLink.headers,
    });

    return {
        uploadToken: uploadLink.upload_token,
    };
}
